'use client';

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { createClient } from '@/lib/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import type { AppUser } from '@/lib/supabase/services';

// ─── Constants ────────────────────────────────────────────────────────────────

const ROLES = ['Administrateur', 'Broker', 'Visiteur'];
const STATUTS = ['Actif', 'Inactif', 'En attente'];

const ROLE_COLORS: Record<string, string> = {
  'Administrateur': 'bg-red-50 text-red-700 border border-red-200',
  'Broker': 'bg-blue-50 text-blue-700 border border-blue-200',
  'Visiteur': 'bg-slate-50 text-slate-600 border border-slate-200',
};

const ROLE_AVATAR_COLORS: Record<string, string> = {
  'Administrateur': 'bg-red-500',
  'Broker': 'bg-blue-500',
  'Visiteur': 'bg-slate-400',
};

const STATUT_COLORS: Record<string, string> = {
  'Actif': 'bg-emerald-50 text-emerald-700 border border-emerald-200',
  'Inactif': 'bg-slate-50 text-slate-500 border border-slate-200',
  'En attente': 'bg-amber-50 text-amber-700 border border-amber-200',
};

const ROLE_ICONS: Record<string, string> = {
  'Administrateur': '🔑',
  'Broker': '🏢',
  'Visiteur': '👁️',
};

const ROLE_PERMISSIONS: Record<string, { label: string; icon: string }[]> = {
  'Administrateur': [
    { label: 'Accès total à toutes les données', icon: '🔑' },
    { label: 'Gestion des utilisateurs et rôles', icon: '👥' },
    { label: 'Paramètres système', icon: '⚙️' },
    { label: 'Tous les locaux, visites, documents', icon: '🏢' },
    { label: 'Rapports et exports complets', icon: '📊' },
  ],
  'Broker': [
    { label: 'Tous les onglets (locaux, enseignes, matching, visites, documents, reporting)', icon: '🏢' },
    { label: 'Ajouter et modifier des locaux', icon: '➕' },
    { label: 'Ajouter et modifier des locataires', icon: '👤' },
    { label: 'Moteur de matching', icon: '⚡' },
    { label: 'Pas d\'accès à la gestion des utilisateurs', icon: '🚫' },
  ],
  'Visiteur': [
    { label: 'Locaux commerciaux uniquement', icon: '👁️' },
    { label: 'Aucun accès aux autres onglets', icon: '🚫' },
    { label: 'Aucun accès au dashboard', icon: '🚫' },
    { label: 'Aucun accès aux documents', icon: '🚫' },
  ],
};

// ─── Types ────────────────────────────────────────────────────────────────────

interface UserFormData {
  prenom: string;
  nom: string;
  email: string;
  role: string;
  password: string;
}

interface BrokerFormData {
  prenom: string;
  nom: string;
  email: string;
  telephone: string;
  role: 'Broker' | 'Broker Senior';
  password: string;
}

interface BrokerLocal {
  id: string;
  ref: string;
  title: string;
  city: string;
  status: string;
}

interface LoginHistoryEntry {
  date: string;
  ip: string;
  device: string;
  status: 'success' | 'failed';
}

const defaultForm: UserFormData = {
  prenom: '',
  nom: '',
  email: '',
  role: 'Broker',
  password: '',
};

const defaultBrokerForm: BrokerFormData = {
  prenom: '',
  nom: '',
  email: '',
  telephone: '',
  role: 'Broker',
  password: '',
};

// ─── Avatar Component ─────────────────────────────────────────────────────────

function UserAvatar({ user, size = 'md' }: { user: AppUser; size?: 'sm' | 'md' | 'lg' }) {
  const sizeClasses = { sm: 'w-8 h-8 text-xs', md: 'w-10 h-10 text-sm', lg: 'w-14 h-14 text-lg' };
  const initials = `${user.prenom?.[0] || ''}${user.nom?.[0] || ''}`.toUpperCase() || user.email?.[0]?.toUpperCase() || '?';
  const bgColor = ROLE_AVATAR_COLORS[user.role] || 'bg-slate-400';

  if (user.avatar) {
    return (
      <img
        src={user.avatar}
        alt={`${user.prenom} ${user.nom}`}
        className={`${sizeClasses[size]} rounded-full object-cover ring-2 ring-white`}
      />
    );
  }

  return (
    <div className={`${sizeClasses[size]} ${bgColor} rounded-full flex items-center justify-center text-white font-semibold ring-2 ring-white`}>
      {initials}
    </div>
  );
}

// ─── Role Badge ───────────────────────────────────────────────────────────────

function RoleBadge({ role }: { role: string }) {
  return (
    <span className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium ${ROLE_COLORS[role] || 'bg-slate-50 text-slate-600 border border-slate-200'}`}>
      <span>{ROLE_ICONS[role] || '👤'}</span>
      {role}
    </span>
  );
}

// ─── Action Dropdown ──────────────────────────────────────────────────────────

function ActionDropdown({
  user,
  onEdit,
  onDelete,
  onViewPortfolio,
  onViewProfile,
  currentUserId,
}: {
  user: AppUser;
  onEdit: () => void;
  onDelete: () => void;
  onViewPortfolio: () => void;
  onViewProfile: () => void;
  currentUserId: string;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const isSelf = user.id === currentUserId;

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen(o => !o)}
        className="p-1.5 rounded-lg hover:bg-slate-100 transition-colors text-slate-400 hover:text-slate-700"
        title="Actions"
      >
        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
          <circle cx="12" cy="5" r="1.5" />
          <circle cx="12" cy="12" r="1.5" />
          <circle cx="12" cy="19" r="1.5" />
        </svg>
      </button>

      {open && (
        <div className="absolute right-0 top-8 z-50 bg-white border border-slate-200 rounded-xl shadow-xl w-56 py-1.5">
          <button
            onClick={() => { onViewProfile(); setOpen(false); }}
            className="w-full flex items-center gap-2.5 px-3.5 py-2 text-sm text-slate-700 hover:bg-slate-50 transition-colors"
          >
            <svg className="w-4 h-4 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
            </svg>
            Voir le profil détaillé
          </button>
          <button
            onClick={() => { onEdit(); setOpen(false); }}
            className="w-full flex items-center gap-2.5 px-3.5 py-2 text-sm text-slate-700 hover:bg-slate-50 transition-colors"
          >
            <svg className="w-4 h-4 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 01-2 2h11a2 2 0 012-2v-5m-1.414-9.414a2 2 0 112.828 2.828L13.828 15H9v-2.828l8.586-8.586z" />
            </svg>
            Modifier le profil
          </button>

          {(user.role === 'Broker' || user.role === 'Broker Senior') && (
            <button
              onClick={() => { onViewPortfolio(); setOpen(false); }}
              className="w-full flex items-center gap-2.5 px-3.5 py-2 text-sm text-slate-700 hover:bg-slate-50 transition-colors"
            >
              <svg className="w-4 h-4 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2z" />
                <polyline points="9 22 9 12 15 12 15 22" />
              </svg>
              Portefeuille de locaux
            </button>
          )}

          {!isSelf && (
            <>
              <div className="border-t border-slate-100 my-1" />
              <button
                onClick={() => { onDelete(); setOpen(false); }}
                className="w-full flex items-center gap-2.5 px-3.5 py-2 text-sm text-red-600 hover:bg-red-50 transition-colors"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                </svg>
                Supprimer l'utilisateur
              </button>
            </>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Detailed Profile Modal ───────────────────────────────────────────────────

function DetailedProfileModal({
  user,
  onClose,
  onRoleChange,
  saving,
}: {
  user: AppUser;
  onClose: () => void;
  onRoleChange: (userId: string, newRole: string) => Promise<void>;
  saving: boolean;
}) {
  const supabase = createClient();
  const [activeTab, setActiveTab] = useState<'info' | 'password' | '2fa' | 'history'>('info');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordMsg, setPasswordMsg] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [changingPassword, setChangingPassword] = useState(false);
  const [twoFAEnabled, setTwoFAEnabled] = useState(false);
  const [selectedRole, setSelectedRole] = useState(user.role);
  const [roleChanged, setRoleChanged] = useState(false);

  const mockLoginHistory: LoginHistoryEntry[] = [
    { date: new Date().toLocaleString('fr-FR'), ip: '196.200.12.45', device: 'Chrome / Windows', status: 'success' },
    { date: new Date(Date.now() - 86400000).toLocaleString('fr-FR'), ip: '196.200.12.45', device: 'Safari / iPhone', status: 'success' },
    { date: new Date(Date.now() - 172800000).toLocaleString('fr-FR'), ip: '41.248.100.22', device: 'Firefox / Linux', status: 'failed' },
    { date: new Date(Date.now() - 259200000).toLocaleString('fr-FR'), ip: '196.200.12.45', device: 'Chrome / Windows', status: 'success' },
    { date: new Date(Date.now() - 345600000).toLocaleString('fr-FR'), ip: '196.200.12.45', device: 'Chrome / macOS', status: 'success' },
  ];

  const handleChangePassword = async () => {
    if (!newPassword || newPassword.length < 8) {
      setPasswordMsg({ type: 'error', text: 'Le mot de passe doit contenir au moins 8 caractères' });
      return;
    }
    if (newPassword !== confirmPassword) {
      setPasswordMsg({ type: 'error', text: 'Les mots de passe ne correspondent pas' });
      return;
    }
    setChangingPassword(true);
    try {
      const { error } = await supabase.auth.updateUser({ password: newPassword });
      if (error) throw error;
      setPasswordMsg({ type: 'success', text: 'Mot de passe modifié avec succès' });
      setNewPassword('');
      setConfirmPassword('');
    } catch (e: any) {
      setPasswordMsg({ type: 'error', text: e?.message || 'Erreur lors du changement de mot de passe' });
    } finally {
      setChangingPassword(false);
    }
  };

  const handleRoleSave = async () => {
    await onRoleChange(user.id, selectedRole);
    setRoleChanged(false);
  };

  const tabs = [
    { key: 'info', label: 'Informations', icon: '👤' },
    { key: 'password', label: 'Mot de passe', icon: '🔒' },
    { key: '2fa', label: 'Sécurité 2FA', icon: '🛡️' },
    { key: 'history', label: 'Historique', icon: '📋' },
  ] as const;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center gap-4 p-6 border-b border-slate-100 bg-gradient-to-r from-slate-50 to-white">
          <UserAvatar user={user} size="lg" />
          <div className="flex-1 min-w-0">
            <h2 className="text-xl font-bold text-slate-900">{user.prenom} {user.nom}</h2>
            <p className="text-sm text-slate-500">{user.email}</p>
            <div className="flex items-center gap-2 mt-1.5">
              <RoleBadge role={user.role} />
            </div>
          </div>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-slate-100 transition-colors text-slate-400">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-slate-100 px-6 bg-white">
          {tabs.map(tab => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`flex items-center gap-1.5 px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                activeTab === tab.key
                  ? 'border-blue-600 text-blue-600' :'border-transparent text-slate-500 hover:text-slate-700'
              }`}
            >
              <span>{tab.icon}</span>
              <span className="hidden sm:inline">{tab.label}</span>
            </button>
          ))}
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {/* Info Tab */}
          {activeTab === 'info' && (
            <div className="space-y-5">
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-slate-50 rounded-xl p-4">
                  <p className="text-xs text-slate-400 mb-1">Prénom</p>
                  <p className="text-sm font-600 text-slate-800">{user.prenom || '—'}</p>
                </div>
                <div className="bg-slate-50 rounded-xl p-4">
                  <p className="text-xs text-slate-400 mb-1">Nom</p>
                  <p className="text-sm font-600 text-slate-800">{user.nom || '—'}</p>
                </div>
                <div className="bg-slate-50 rounded-xl p-4 col-span-2">
                  <p className="text-xs text-slate-400 mb-1">Email</p>
                  <p className="text-sm font-600 text-slate-800">{user.email}</p>
                </div>
                <div className="bg-slate-50 rounded-xl p-4">
                  <p className="text-xs text-slate-400 mb-1">Locaux assignés</p>
                  <p className="text-2xl font-bold text-blue-600">{user.nbLocaux}</p>
                </div>
                <div className="bg-slate-50 rounded-xl p-4">
                  <p className="text-xs text-slate-400 mb-1">Visites</p>
                  <p className="text-2xl font-bold text-emerald-600">{user.nbVisites}</p>
                </div>
                <div className="bg-slate-50 rounded-xl p-4">
                  <p className="text-xs text-slate-400 mb-1">Dernière connexion</p>
                  <p className="text-sm font-600 text-slate-800">{user.derniereConnexion}</p>
                </div>
                <div className="bg-slate-50 rounded-xl p-4">
                  <p className="text-xs text-slate-400 mb-1">Membre depuis</p>
                  <p className="text-sm font-600 text-slate-800">{user.dateCreation || '—'}</p>
                </div>
              </div>

              {/* Role assignment */}
              <div className="border border-slate-200 rounded-xl p-4">
                <p className="text-sm font-600 text-slate-700 mb-3">Assigner un rôle</p>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mb-3">
                  {ROLES.map(role => (
                    <button
                      key={role}
                      onClick={() => { setSelectedRole(role); setRoleChanged(role !== user.role); }}
                      className={`flex items-center gap-2 px-3 py-2.5 rounded-xl border text-xs font-medium transition-all ${
                        selectedRole === role
                          ? 'border-blue-400 bg-blue-50 text-blue-700 shadow-sm'
                          : 'border-slate-200 text-slate-600 hover:border-slate-300 hover:bg-slate-50'
                      }`}
                    >
                      <span>{ROLE_ICONS[role]}</span>
                      <span>{role}</span>
                    </button>
                  ))}
                </div>
                {ROLE_PERMISSIONS[selectedRole] && (
                  <div className="bg-slate-50 rounded-lg p-3 mb-3">
                    <p className="text-xs font-600 text-slate-500 mb-2 uppercase tracking-wide">Permissions</p>
                    <ul className="space-y-1">
                      {ROLE_PERMISSIONS[selectedRole].map((p, i) => (
                        <li key={i} className="flex items-center gap-2 text-xs text-slate-600">
                          <span>{p.icon}</span><span>{p.label}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                {roleChanged && (
                  <button
                    onClick={handleRoleSave}
                    disabled={saving}
                    className="w-full px-4 py-2.5 bg-blue-600 text-white rounded-xl text-sm font-medium hover:bg-blue-700 transition-colors disabled:opacity-50"
                  >
                    {saving ? 'Enregistrement...' : `Appliquer le rôle "${selectedRole}"`}
                  </button>
                )}
              </div>
            </div>
          )}

          {/* Password Tab */}
          {activeTab === 'password' && (
            <div className="space-y-4 max-w-md">
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 text-sm text-amber-700">
                <p className="font-600 mb-1">⚠️ Changement de mot de passe</p>
                <p>Cette action modifie le mot de passe du compte connecté. Pour changer le mot de passe d'un autre utilisateur, utilisez la console Supabase.</p>
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1.5">Nouveau mot de passe</label>
                <input
                  type="password"
                  value={newPassword}
                  onChange={e => setNewPassword(e.target.value)}
                  className="w-full px-4 py-2.5 rounded-xl border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Minimum 8 caractères"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1.5">Confirmer le mot de passe</label>
                <input
                  type="password"
                  value={confirmPassword}
                  onChange={e => setConfirmPassword(e.target.value)}
                  className="w-full px-4 py-2.5 rounded-xl border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Répétez le mot de passe"
                />
              </div>
              {passwordMsg && (
                <div className={`p-3 rounded-xl text-sm ${passwordMsg.type === 'success' ? 'bg-emerald-50 text-emerald-700 border border-emerald-200' : 'bg-red-50 text-red-700 border border-red-200'}`}>
                  {passwordMsg.text}
                </div>
              )}
              <button
                onClick={handleChangePassword}
                disabled={changingPassword || !newPassword || !confirmPassword}
                className="w-full px-4 py-2.5 bg-blue-600 text-white rounded-xl text-sm font-medium hover:bg-blue-700 transition-colors disabled:opacity-50"
              >
                {changingPassword ? 'Modification...' : 'Changer le mot de passe'}
              </button>
            </div>
          )}

          {/* 2FA Tab */}
          {activeTab === '2fa' && (
            <div className="space-y-5 max-w-md">
              <div className="bg-white border border-slate-200 rounded-xl p-5">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${twoFAEnabled ? 'bg-emerald-100' : 'bg-slate-100'}`}>
                      <svg className={`w-5 h-5 ${twoFAEnabled ? 'text-emerald-600' : 'text-slate-400'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                      </svg>
                    </div>
                    <div>
                      <p className="text-sm font-600 text-slate-800">Authentification à deux facteurs</p>
                      <p className="text-xs text-slate-500">{twoFAEnabled ? 'Activée — compte sécurisé' : 'Désactivée — recommandé d\'activer'}</p>
                    </div>
                  </div>
                  <button
                    onClick={() => setTwoFAEnabled(!twoFAEnabled)}
                    className={`relative w-12 h-6 rounded-full transition-colors ${twoFAEnabled ? 'bg-emerald-500' : 'bg-slate-300'}`}
                  >
                    <span className={`absolute top-1 w-4 h-4 bg-white rounded-full shadow transition-transform ${twoFAEnabled ? 'translate-x-7' : 'translate-x-1'}`} />
                  </button>
                </div>
                {twoFAEnabled ? (
                  <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4 text-sm text-emerald-700">
                    <p className="font-600 mb-1">✅ 2FA activée</p>
                    <p>Ce compte est protégé par l'authentification à deux facteurs. Un code sera demandé à chaque connexion.</p>
                  </div>
                ) : (
                  <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 text-sm text-amber-700">
                    <p className="font-600 mb-1">⚠️ 2FA désactivée</p>
                    <p>Activez la 2FA pour renforcer la sécurité de ce compte. Un code OTP sera envoyé par email ou application.</p>
                  </div>
                )}
              </div>
              <div className="bg-slate-50 rounded-xl p-4 text-xs text-slate-500 space-y-2">
                <p className="font-600 text-slate-600">Méthodes disponibles :</p>
                <div className="flex items-center gap-2"><span>📱</span><span>Application d'authentification (TOTP)</span></div>
                <div className="flex items-center gap-2"><span>📧</span><span>Code par email</span></div>
                <div className="flex items-center gap-2"><span>💬</span><span>SMS (bientôt disponible)</span></div>
              </div>
            </div>
          )}

          {/* History Tab */}
          {activeTab === 'history' && (
            <div className="space-y-3">
              <div className="flex items-center justify-between mb-2">
                <p className="text-sm font-600 text-slate-700">Dernières connexions</p>
                <span className="text-xs text-slate-400">{mockLoginHistory.length} entrées</span>
              </div>
              {mockLoginHistory.map((entry, i) => (
                <div key={i} className={`flex items-center gap-4 p-4 rounded-xl border ${entry.status === 'success' ? 'bg-white border-slate-100' : 'bg-red-50 border-red-100'}`}>
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${entry.status === 'success' ? 'bg-emerald-100' : 'bg-red-100'}`}>
                    {entry.status === 'success' ? (
                      <svg className="w-4 h-4 text-emerald-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                    ) : (
                      <svg className="w-4 h-4 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-500 text-slate-800">{entry.device}</p>
                    <p className="text-xs text-slate-400">{entry.ip} · {entry.date}</p>
                  </div>
                  <span className={`text-xs font-600 px-2 py-1 rounded-full ${entry.status === 'success' ? 'bg-emerald-50 text-emerald-700' : 'bg-red-50 text-red-600'}`}>
                    {entry.status === 'success' ? 'Succès' : 'Échec'}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── User Form Modal ──────────────────────────────────────────────────────────

function UserFormModal({
  open,
  onClose,
  onSave,
  editUser,
  loading,
}: {
  open: boolean;
  onClose: () => void;
  onSave: (data: UserFormData) => void;
  editUser: AppUser | null;
  loading: boolean;
}) {
  const [form, setForm] = useState<UserFormData>(defaultForm);
  const [errors, setErrors] = useState<Partial<UserFormData>>({});

  useEffect(() => {
    if (editUser) {
      setForm({
        prenom: editUser.prenom || '',
        nom: editUser.nom || '',
        email: editUser.email || '',
        role: editUser.role || 'Broker',
        password: '',
      });
    } else {
      setForm(defaultForm);
    }
    setErrors({});
  }, [editUser, open]);

  const validate = () => {
    const e: Partial<UserFormData> = {};
    if (!form.prenom.trim()) e.prenom = 'Prénom requis';
    if (!form.nom.trim()) e.nom = 'Nom requis';
    if (!form.email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) e.email = 'Email valide requis';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (validate()) onSave(form);
  };

  if (!open || !editUser) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b border-slate-100">
          <div>
            <h2 className="text-lg font-semibold text-slate-900">
              Modifier le profil
            </h2>
            <p className="text-sm text-slate-500 mt-0.5">
              Édition de {editUser.prenom} {editUser.nom}
            </p>
          </div>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-slate-100 transition-colors text-slate-400">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1.5">Prénom *</label>
              <input
                type="text"
                value={form.prenom}
                onChange={e => setForm(f => ({ ...f, prenom: e.target.value }))}
                className={`w-full px-4 py-2.5 rounded-lg border text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${errors.prenom ? 'border-red-300 bg-red-50' : 'border-slate-200 bg-white'}`}
                placeholder="Prénom"
              />
              {errors.prenom && <p className="text-xs text-red-500 mt-1">{errors.prenom}</p>}
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1.5">Nom *</label>
              <input
                type="text"
                value={form.nom}
                onChange={e => setForm(f => ({ ...f, nom: e.target.value }))}
                className={`w-full px-4 py-2.5 rounded-lg border text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${errors.nom ? 'border-red-300 bg-red-50' : 'border-slate-200 bg-white'}`}
                placeholder="Nom de famille"
              />
              {errors.nom && <p className="text-xs text-red-500 mt-1">{errors.nom}</p>}
            </div>
          </div>

          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1.5">Adresse email</label>
            <input
              type="email"
              value={form.email}
              disabled
              className="w-full px-4 py-2.5 rounded-lg border text-sm border-slate-200 bg-slate-50 text-slate-400 cursor-not-allowed"
            />
            <p className="text-xs text-slate-400 mt-1">L'email ne peut pas être modifié</p>
          </div>

          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1.5">Rôle *</label>
            <select
              value={form.role}
              onChange={e => setForm(f => ({ ...f, role: e.target.value }))}
              className="w-full px-3 py-2.5 rounded-lg border border-slate-200 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              {ROLES.map(r => (
                <option key={r} value={r}>{ROLE_ICONS[r]} {r}</option>
              ))}
            </select>
          </div>

          {ROLE_PERMISSIONS[form.role] && (
            <div className="bg-slate-50 rounded-xl p-3.5 border border-slate-100">
              <p className="text-xs font-semibold text-slate-600 mb-2 uppercase tracking-wide">Permissions du rôle</p>
              <ul className="space-y-1">
                {ROLE_PERMISSIONS[form.role].map((p, i) => (
                  <li key={i} className="flex items-center gap-2 text-xs text-slate-600">
                    <span>{p.icon}</span><span>{p.label}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          <div className="flex gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2.5 rounded-xl border border-slate-200 text-sm font-medium text-slate-700 hover:bg-slate-50 transition-colors"
            >
              Annuler
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex-1 px-4 py-2.5 rounded-xl bg-blue-600 text-white text-sm font-medium hover:bg-blue-700 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {loading ? (
                <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                </svg>
              ) : null}
              {loading ? 'Enregistrement...' : 'Enregistrer'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ─── Portfolio Modal ──────────────────────────────────────────────────────────

function PortfolioModal({
  user,
  locaux,
  onClose,
}: {
  user: AppUser;
  locaux: BrokerLocal[];
  onClose: () => void;
}) {
  const statusColors: Record<string, string> = {
    'Publié': 'bg-emerald-50 text-emerald-700',
    'Brouillon': 'bg-slate-50 text-slate-500',
    'En revue': 'bg-amber-50 text-amber-700',
    'Loué / retiré': 'bg-red-50 text-red-600',
    'À fort potentiel': 'bg-blue-50 text-blue-700',
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[80vh] overflow-hidden flex flex-col">
        <div className="flex items-center justify-between p-6 border-b border-slate-100">
          <div className="flex items-center gap-3">
            <UserAvatar user={user} size="md" />
            <div>
              <h2 className="text-lg font-semibold text-slate-900">Portefeuille de {user.prenom} {user.nom}</h2>
              <div className="flex items-center gap-2 mt-0.5">
                <RoleBadge role={user.role} />
                <span className="text-sm text-slate-500">{locaux.length} local{locaux.length !== 1 ? 'aux' : ''}</span>
              </div>
            </div>
          </div>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-slate-100 transition-colors text-slate-400">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        <div className="overflow-y-auto flex-1 p-6">
          {locaux.length === 0 ? (
            <div className="text-center py-12">
              <div className="w-16 h-16 bg-slate-100 rounded-2xl flex items-center justify-center">
                <svg className="w-8 h-8 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2z" />
                </svg>
              </div>
              <p className="text-slate-500 font-medium">Aucun local assigné</p>
              <p className="text-sm text-slate-400 mt-1">Ce broker n'a pas encore de locaux dans son portefeuille</p>
            </div>
          ) : (
            <div className="space-y-2">
              {locaux.map(local => (
                <div key={local.id} className="flex items-center justify-between p-3.5 bg-slate-50 rounded-xl border border-slate-100 hover:bg-slate-100 transition-colors">
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 bg-blue-100 rounded-lg flex items-center justify-center">
                      <svg className="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2z" />
                      </svg>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-slate-800">{local.title}</p>
                      <p className="text-xs text-slate-500">{local.ref} · {local.city}</p>
                    </div>
                  </div>
                  <span className={`text-xs font-medium px-2.5 py-1 rounded-full ${statusColors[local.status] || 'bg-slate-50 text-slate-500'}`}>
                    {local.status}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Delete Confirm Modal ─────────────────────────────────────────────────────

function DeleteConfirmModal({
  user,
  onConfirm,
  onClose,
  loading,
}: {
  user: AppUser;
  onConfirm: () => void;
  onClose: () => void;
  loading: boolean;
}) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md p-6">
        <div className="flex items-center gap-4 mb-4">
          <div className="w-12 h-12 bg-red-100 rounded-xl flex items-center justify-center">
            <svg className="w-6 h-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
          </div>
          <div>
            <h3 className="text-lg font-semibold text-slate-900">Supprimer l'utilisateur</h3>
            <p className="text-sm text-slate-500">Cette action est irréversible</p>
          </div>
        </div>
        <p className="text-sm text-slate-600 mb-6">
          Êtes-vous sûr de vouloir supprimer <strong>{user.prenom} {user.nom}</strong> ({user.email}) ?
          Son profil sera définitivement supprimé de la base de données.
        </p>
        <div className="flex gap-3">
          <button
            onClick={onClose}
            className="flex-1 px-4 py-2.5 rounded-xl border border-slate-200 text-sm font-medium text-slate-700 hover:bg-slate-50 transition-colors"
          >
            Annuler
          </button>
          <button
            onClick={onConfirm}
            disabled={loading}
            className="flex-1 px-4 py-2.5 rounded-xl bg-red-600 text-white text-sm font-medium hover:bg-red-700 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
          >
            {loading ? (
              <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
              </svg>
            ) : null}
            Supprimer définitivement
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Stats Card ───────────────────────────────────────────────────────────────

function StatsCard({ label, value, icon, color }: { label: string; value: number | string; icon: string; color: string }) {
  return (
    <div className="bg-white rounded-2xl border border-slate-100 p-5 shadow-sm">
      <div className="flex items-center justify-between mb-3">
        <span className="text-2xl">{icon}</span>
        <span className={`text-2xl font-bold ${color}`}>{value}</span>
      </div>
      <p className="text-sm text-slate-500 font-medium">{label}</p>
    </div>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────

const UtilisateursContent: React.FC = () => {
  const { userProfile, isAdmin, user: currentUser } = useAuth();
  const supabase = createClient();

  const [users, setUsers] = useState<AppUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMsg, setSuccessMsg] = useState<string | null>(null);

  const [search, setSearch] = useState('');
  const [filterRole, setFilterRole] = useState('');
  const [filterStatut, setFilterStatut] = useState('');
  const [selectedUsers, setSelectedUsers] = useState<string[]>([]);
  const [bulkRole, setBulkRole] = useState('');
  const [showBulkConfirm, setShowBulkConfirm] = useState(false);

  const [showForm, setShowForm] = useState(false);
  const [editUser, setEditUser] = useState<AppUser | null>(null);
  const [deleteUser, setDeleteUser] = useState<AppUser | null>(null);
  const [portfolioUser, setPortfolioUser] = useState<AppUser | null>(null);
  const [portfolioLocaux, setPortfolioLocaux] = useState<BrokerLocal[]>([]);
  const [portfolioLoading, setPortfolioLoading] = useState(false);
  const [profileUser, setProfileUser] = useState<AppUser | null>(null);

  const canManage = isAdmin;

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { data, error: err } = await supabase
        .from('user_profiles')
        .select('*')
        .order('created_at', { ascending: false });

      if (err) throw err;

      const profiles = data || [];

      // Fetch real locaux counts per broker_id
      const { data: locauxData } = await supabase
        .from('locaux')
        .select('broker_id');

      // Fetch real visites counts per broker_id
      const { data: visitesData } = await supabase
        .from('visites')
        .select('broker_id');

      // Build count maps
      const locauxCountMap: Record<string, number> = {};
      (locauxData || []).forEach((row: any) => {
        if (row.broker_id) {
          locauxCountMap[row.broker_id] = (locauxCountMap[row.broker_id] || 0) + 1;
        }
      });

      const visitesCountMap: Record<string, number> = {};
      (visitesData || []).forEach((row: any) => {
        if (row.broker_id) {
          visitesCountMap[row.broker_id] = (visitesCountMap[row.broker_id] || 0) + 1;
        }
      });

      const mapped: AppUser[] = profiles.map((row: any) => ({
        id: row.id,
        nom: row.nom || '',
        prenom: row.prenom || '',
        email: row.email || '',
        role: row.role as AppUser['role'],
        nbLocaux: locauxCountMap[row.id] ?? Number(row.nb_locaux) ?? 0,
        nbVisites: visitesCountMap[row.id] ?? Number(row.nb_visites) ?? 0,
        derniereConnexion: row.derniere_connexion || 'Jamais',
        dateCreation: row.date_creation || row.created_at || '',
        avatar: row.avatar || '',
      }));

      setUsers(mapped);
    } catch (e: any) {
      setError('Erreur lors du chargement des utilisateurs : ' + (e?.message || 'Erreur inconnue'));
    } finally {
      setLoading(false);
    }
  }, [supabase]);

  useEffect(() => { fetchUsers(); }, [fetchUsers]);

  const filteredUsers = users.filter(u => {
    const q = search.toLowerCase();
    const matchSearch = !q || `${u.prenom} ${u.nom} ${u.email}`.toLowerCase().includes(q);
    const matchRole = !filterRole || u.role === filterRole;
    const matchStatut = !filterStatut || filterStatut === '';
    return matchSearch && matchRole && matchStatut;
  });

  const stats = {
    total: users.length,
    actifs: users.length,
    admins: users.filter(u => u.role === 'Administrateur').length,
    brokers: users.filter(u => u.role === 'Broker').length,
    visiteurs: users.filter(u => u.role === 'Visiteur').length,
    enAttente: 0,
  };

  const handleUpdate = async (form: UserFormData) => {
    if (!editUser) return;
    setSaving(true);
    setError(null);
    try {
      const { error: err } = await supabase
        .from('user_profiles')
        .update({ nom: form.nom, prenom: form.prenom, role: form.role })
        .eq('id', editUser.id);
      if (err) throw err;
      setSuccessMsg(`Profil de ${form.prenom} ${form.nom} mis à jour.`);
      setShowForm(false);
      setEditUser(null);
      await fetchUsers();
    } catch (e: any) {
      setError('Erreur mise à jour : ' + (e?.message || 'Erreur inconnue'));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteUser) return;
    setSaving(true);
    setError(null);
    try {
      const { error: err } = await supabase.from('user_profiles').delete().eq('id', deleteUser.id);
      if (err) throw err;
      setSuccessMsg(`Utilisateur ${deleteUser.prenom} ${deleteUser.nom} supprimé.`);
      setDeleteUser(null);
      await fetchUsers();
    } catch (e: any) {
      setError('Erreur suppression : ' + (e?.message || 'Erreur inconnue'));
    } finally {
      setSaving(false);
    }
  };

  const handleRoleChange = async (userId: string, newRole: string) => {
    setSaving(true);
    try {
      const { error: err } = await supabase.from('user_profiles').update({ role: newRole }).eq('id', userId);
      if (err) throw err;
      setSuccessMsg(`Rôle mis à jour : ${newRole}`);
      await fetchUsers();
      // Update profileUser if open
      if (profileUser?.id === userId) {
        setProfileUser(prev => prev ? { ...prev, role: newRole as AppUser['role'] } : null);
      }
    } catch (e: any) {
      setError('Erreur changement de rôle : ' + (e?.message || ''));
    } finally {
      setSaving(false);
    }
  };

  const handleBulkRoleAssign = async () => {
    if (!bulkRole || selectedUsers.length === 0) return;
    setSaving(true);
    try {
      const { error: err } = await supabase
        .from('user_profiles')
        .update({ role: bulkRole })
        .in('id', selectedUsers);
      if (err) throw err;
      setSuccessMsg(`Rôle "${bulkRole}" assigné à ${selectedUsers.length} utilisateur(s).`);
      setSelectedUsers([]);
      setBulkRole('');
      setShowBulkConfirm(false);
      await fetchUsers();
    } catch (e: any) {
      setError('Erreur assignation en masse : ' + (e?.message || ''));
    } finally {
      setSaving(false);
    }
  };

  const handleViewPortfolio = async (u: AppUser) => {
    setPortfolioUser(u);
    setPortfolioLoading(true);
    try {
      const { data, error: err } = await supabase
        .from('locaux')
        .select('id, ref, title, city, status')
        .eq('broker_id', u.id);
      if (err) throw err;
      setPortfolioLocaux((data || []).map((r: any) => ({ id: r.id, ref: r.ref, title: r.title, city: r.city, status: r.status })));
    } catch {
      setPortfolioLocaux([]);
    } finally {
      setPortfolioLoading(false);
    }
  };

  useEffect(() => {
    if (successMsg) {
      const t = setTimeout(() => setSuccessMsg(null), 4000);
      return () => clearTimeout(t);
    }
  }, [successMsg]);

  const roleGroups = ROLES.map(role => ({
    role,
    count: users.filter(u => u.role === role).length,
    actifs: users.filter(u => u.role === role).length,
  }));

  const toggleSelectUser = (id: string) => {
    setSelectedUsers(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };

  const toggleSelectAll = () => {
    if (selectedUsers.length === filteredUsers.length) {
      setSelectedUsers([]);
    } else {
      setSelectedUsers(filteredUsers.map(u => u.id));
    }
  };

  if (!canManage) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] gap-4">
        <div className="w-20 h-20 bg-red-50 rounded-2xl flex items-center justify-center">
          <svg className="w-10 h-10 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 15v2m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        </div>
        <h2 className="text-xl font-semibold text-slate-800">Accès restreint</h2>
        <p className="text-slate-500 text-center max-w-sm">
          La gestion des utilisateurs est réservée aux Administrateurs et Brokers Senior.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* ── Page Header ── */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Gestion des utilisateurs</h1>
          <p className="text-slate-500 mt-1 text-sm">
            {users.length} utilisateur{users.length !== 1 ? 's' : ''} · {stats.actifs} actif{stats.actifs !== 1 ? 's' : ''}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <div className="inline-flex items-center gap-2 px-4 py-2.5 bg-amber-50 text-amber-700 border border-amber-200 rounded-xl text-sm">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4m0 0h-1m1 1v4m1 0h1m1 0v-4m1 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            Créez les utilisateurs directement dans Supabase Auth
          </div>
        </div>
      </div>

      {/* ── Notifications ── */}
      {error && (
        <div className="flex items-start gap-3 p-4 bg-red-50 border border-red-200 rounded-xl text-red-700 text-sm">
          <svg className="w-5 h-5 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <span>{error}</span>
          <button onClick={() => setError(null)} className="ml-auto text-red-400 hover:text-red-600">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      )}
      {successMsg && (
        <div className="flex items-center gap-3 p-4 bg-emerald-50 border border-emerald-200 rounded-xl text-emerald-700 text-sm">
          <svg className="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          {successMsg}
        </div>
      )}

      {/* ── Stats Grid ── */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
        <StatsCard label="Total" value={stats.total} icon="👥" color="text-slate-800" />
        <StatsCard label="Actifs" value={stats.actifs} icon="✅" color="text-emerald-600" />
        <StatsCard label="Admins" value={stats.admins} icon="🔑" color="text-red-600" />
        <StatsCard label="Brokers" value={stats.brokers} icon="🏢" color="text-blue-600" />
        <StatsCard label="Visiteurs" value={stats.visiteurs} icon="👁️" color="text-slate-600" />
      </div>

      {/* ── Role Overview Cards ── */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
        {roleGroups.map(({ role, count, actifs }) => (
          <button
            key={role}
            onClick={() => setFilterRole(filterRole === role ? '' : role)}
            className={`p-4 rounded-2xl border text-left transition-all ${
              filterRole === role
                ? 'border-blue-300 bg-blue-50 shadow-sm'
                : 'border-slate-100 bg-white hover:border-slate-200 hover:shadow-sm'
            }`}
          >
            <div className="text-2xl mb-2">{ROLE_ICONS[role]}</div>
            <p className="text-xs font-medium text-slate-500 leading-tight">{role}</p>
            <p className="text-2xl font-bold text-slate-800 mt-1">{count}</p>
            <p className="text-xs text-slate-400 mt-0.5">{actifs} actif{actifs !== 1 ? 's' : ''}</p>
          </button>
        ))}
      </div>

      {/* ── Bulk Role Assignment Banner ── */}
      {selectedUsers.length > 0 && canManage && (
        <div className="flex items-center gap-4 p-4 bg-blue-50 border border-blue-200 rounded-xl">
          <span className="text-sm font-600 text-blue-700">{selectedUsers.length} utilisateur(s) sélectionné(s)</span>
          <div className="flex items-center gap-2 ml-auto">
            <select
              value={bulkRole}
              onChange={e => setBulkRole(e.target.value)}
              className="px-3 py-2 rounded-lg border border-blue-200 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">Choisir un rôle...</option>
              {ROLES.map(r => <option key={r} value={r}>{ROLE_ICONS[r]} {r}</option>)}
            </select>
            <button
              onClick={() => bulkRole && setShowBulkConfirm(true)}
              disabled={!bulkRole}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 transition-colors disabled:opacity-50"
            >
              Assigner
            </button>
            <button
              onClick={() => setSelectedUsers([])}
              className="px-3 py-2 border border-blue-200 text-blue-600 rounded-lg text-sm hover:bg-blue-100 transition-colors"
            >
              Annuler
            </button>
          </div>
        </div>
      )}

      {/* Bulk confirm */}
      {showBulkConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={() => setShowBulkConfirm(false)} />
          <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md p-6">
            <h3 className="text-lg font-semibold text-slate-900 mb-2">Confirmer l'assignation en masse</h3>
            <p className="text-sm text-slate-600 mb-5">
              Assigner le rôle <strong>{bulkRole}</strong> à <strong>{selectedUsers.length}</strong> utilisateur(s) ?
              Cette action modifiera leurs permissions immédiatement.
            </p>
            <div className="flex gap-3">
              <button onClick={() => setShowBulkConfirm(false)} className="flex-1 px-4 py-2.5 rounded-xl border border-slate-200 text-sm font-medium text-slate-700 hover:bg-slate-50 transition-colors">Annuler</button>
              <button onClick={handleBulkRoleAssign} disabled={saving} className="flex-1 px-4 py-2.5 rounded-xl bg-blue-600 text-white text-sm font-medium hover:bg-blue-700 transition-colors disabled:opacity-50">
                {saving ? 'En cours...' : 'Confirmer'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Filters ── */}
      <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-4">
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1">
            <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
            <input
              type="text"
              placeholder="Rechercher par nom, prénom, email..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="w-full pl-9 pr-4 py-2.5 rounded-xl border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-slate-50"
            />
          </div>
          <select
            value={filterRole}
            onChange={e => setFilterRole(e.target.value)}
            className="px-3 py-2.5 rounded-xl border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-slate-50 min-w-[160px]"
          >
            <option value="">Tous les rôles</option>
            {ROLES.map(r => <option key={r} value={r}>{r}</option>)}
          </select>
          <select
            value={filterStatut}
            onChange={e => setFilterStatut(e.target.value)}
            className="px-3 py-2.5 rounded-xl border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-slate-50 min-w-[140px]"
          >
            <option value="">Tous les statuts</option>
            {STATUTS.map(s => <option key={s} value={s}>{s}</option>)}
          </select>
          {(search || filterRole || filterStatut) && (
            <button
              onClick={() => { setSearch(''); setFilterRole(''); setFilterStatut(''); }}
              className="px-3 py-2.5 rounded-xl border border-slate-200 text-sm text-slate-500 hover:bg-slate-50 transition-colors whitespace-nowrap"
            >
              Réinitialiser
            </button>
          )}
        </div>
        {(search || filterRole || filterStatut) && (
          <p className="text-xs text-slate-500 mt-2.5">
            {filteredUsers.length} résultat{filteredUsers.length !== 1 ? 's' : ''} sur {users.length} utilisateurs
          </p>
        )}
      </div>

      {/* ── Users Table ── */}
      <div className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden">
        {loading ? (
          <div className="flex flex-col items-center justify-center py-20 gap-4">
            <svg className="w-8 h-8 animate-spin text-blue-500" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
            </svg>
            <p className="text-slate-500 text-sm">Chargement des utilisateurs...</p>
          </div>
        ) : filteredUsers.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20 gap-4">
            <div className="w-16 h-16 bg-slate-100 rounded-2xl flex items-center justify-center">
              <svg className="w-8 h-8 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2z" />
              </svg>
            </div>
            <div className="text-center">
              <p className="text-slate-700 font-medium">Aucun utilisateur trouvé</p>
              <p className="text-sm text-slate-400 mt-1">
                {search || filterRole || filterStatut ? 'Modifiez vos filtres pour voir plus de résultats' : 'Ajoutez des utilisateurs depuis Supabase Auth'}
              </p>
            </div>
          </div>
        ) : (
          <>
            <div className="hidden lg:grid grid-cols-[auto_2.5fr_1.5fr_1fr_1fr_1fr_1fr_auto] gap-4 px-6 py-3 bg-slate-50 border-b border-slate-100 text-xs font-semibold text-slate-500 uppercase tracking-wide">
              {canManage && (
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    checked={selectedUsers.length === filteredUsers.length && filteredUsers.length > 0}
                    onChange={toggleSelectAll}
                    className="w-4 h-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                  />
                </div>
              )}
              <span>Utilisateur</span>
              <span>Rôle</span>
              <span>Locaux</span>
              <span>Visites</span>
              <span>Dernière connexion</span>
              <span></span>
            </div>

            <div className="divide-y divide-slate-50">
              {filteredUsers.map(u => (
                <div
                  key={u.id}
                  className={`flex flex-col lg:grid lg:grid-cols-[auto_2.5fr_1.5fr_1fr_1fr_1fr_1fr_auto] gap-3 lg:gap-4 px-6 py-4 hover:bg-slate-50/70 transition-colors ${u.id === currentUser?.id ? 'bg-blue-50/30' : ''} ${selectedUsers.includes(u.id) ? 'bg-blue-50/50' : ''}`}
                >
                  {canManage && (
                    <div className="hidden lg:flex items-center">
                      <input
                        type="checkbox"
                        checked={selectedUsers.includes(u.id)}
                        onChange={() => toggleSelectUser(u.id)}
                        className="w-4 h-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                      />
                    </div>
                  )}
                  <div className="flex items-center gap-3">
                    <UserAvatar user={u} size="md" />
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <p className="text-sm font-semibold text-slate-800 truncate">{u.prenom} {u.nom}</p>
                        {u.id === currentUser?.id && (
                          <span className="text-xs bg-blue-100 text-blue-600 px-1.5 py-0.5 rounded-full font-medium">Vous</span>
                        )}
                      </div>
                      <p className="text-xs text-slate-400 truncate">{u.email}</p>
                      {u.dateCreation && <p className="text-xs text-slate-300 mt-0.5">Créé le {u.dateCreation}</p>}
                    </div>
                  </div>
                  <div className="flex items-center"><RoleBadge role={u.role} /></div>
                  <div className="flex items-center">
                    <div className="flex items-center gap-1.5">
                      <svg className="w-3.5 h-3.5 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2z" />
                      </svg>
                      <span className="text-sm font-medium text-slate-700">{u.nbLocaux}</span>
                    </div>
                  </div>
                  <div className="flex items-center">
                    <div className="flex items-center gap-1.5">
                      <svg className="w-3.5 h-3.5 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                      </svg>
                      <span className="text-sm font-medium text-slate-700">{u.nbVisites}</span>
                    </div>
                  </div>
                  <div className="flex items-center">
                    <span className="text-xs text-slate-500">{u.derniereConnexion}</span>
                  </div>
                  <div className="flex items-center justify-end">
                    {canManage ? (
                      <ActionDropdown
                        user={u}
                        onEdit={() => { setEditUser(u); setShowForm(true); }}
                        onDelete={() => setDeleteUser(u)}
                        onViewPortfolio={() => handleViewPortfolio(u)}
                        onViewProfile={() => setProfileUser(u)}
                        currentUserId={currentUser?.id || ''}
                      />
                    ) : (u.role === 'Broker' || u.role === 'Broker Senior') ? (
                      <button
                        onClick={() => handleViewPortfolio(u)}
                        className="p-1.5 rounded-lg hover:bg-slate-100 transition-colors text-slate-400 hover:text-blue-600"
                        title="Voir le portefeuille"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2z" />
                        </svg>
                      </button>
                    ) : null}
                  </div>
                </div>
              ))}
            </div>

            <div className="px-6 py-3 bg-slate-50 border-t border-slate-100 flex items-center justify-between">
              <p className="text-xs text-slate-500">
                {filteredUsers.length} utilisateur{filteredUsers.length !== 1 ? 's' : ''} affiché{filteredUsers.length !== 1 ? 's' : ''}
              </p>
              <button
                onClick={fetchUsers}
                className="flex items-center gap-1.5 text-xs text-slate-500 hover:text-slate-700 transition-colors"
              >
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                </svg>
                Actualiser
              </button>
            </div>
          </>
        )}
      </div>

      {/* ── Permissions Matrix ── */}
      <div className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden">
        <div className="px-6 py-4 border-b border-slate-100">
          <h2 className="text-base font-semibold text-slate-800">Matrice des permissions par rôle</h2>
          <p className="text-sm text-slate-500 mt-0.5">Vue d'ensemble des droits d'accès selon le rôle — appliqués automatiquement à la connexion</p>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-slate-50 border-b border-slate-100">
                <th className="text-left px-6 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wide">Ressource</th>
                {['Administrateur', 'Broker Senior', 'Broker', 'Propriétaire', 'Locataire', 'Visiteur'].map(role => (
                  <th key={role} className="text-center px-3 py-3">
                    <RoleBadge role={role} />
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-50">
              {[
                { resource: 'Dashboard', admin: '✅', senior: '✅', broker: '✅', owner: '✅', tenant: '✅', visitor: '❌' },
                { resource: 'Locaux commerciaux', admin: '✅ Tous', senior: '✅ Tous', broker: '⚠️ Assignés', owner: '⚠️ Propres', tenant: '⚠️ Publiés', visitor: '✅ Publiés' },
                { resource: 'Enseignes', admin: '✅', senior: '✅', broker: '✅', owner: '❌', tenant: '❌', visitor: '❌' },
                { resource: 'Matching', admin: '✅', senior: '✅', broker: '✅', owner: '❌', tenant: '❌', visitor: '❌' },
                { resource: 'Visites', admin: '✅ Toutes', senior: '✅ Toutes', broker: '⚠️ Assignées', owner: '❌', tenant: '⚠️ Propres', visitor: '❌' },
                { resource: 'Documents', admin: '✅ Tous', senior: '✅ Tous', broker: '⚠️ Non-privés', owner: '⚠️ Owner+', tenant: '⚠️ Tenant', visitor: '❌' },
                { resource: 'Reporting', admin: '✅', senior: '✅', broker: '✅', owner: '❌', tenant: '❌', visitor: '❌' },
                { resource: 'Utilisateurs', admin: '✅ Gestion', senior: '👁️ Lecture', broker: '❌', owner: '❌', tenant: '❌', visitor: '❌' },
                { resource: 'Audit Logs', admin: '✅', senior: '✅', broker: '❌', owner: '❌', tenant: '❌', visitor: '❌' },
                { resource: 'Paramètres', admin: '✅', senior: '❌', broker: '❌', owner: '❌', tenant: '❌', visitor: '❌' },
              ].map(row => (
                <tr key={row.resource} className="hover:bg-slate-50/50 transition-colors">
                  <td className="px-6 py-3 font-medium text-slate-700">{row.resource}</td>
                  <td className="px-3 py-3 text-center text-xs">{row.admin}</td>
                  <td className="px-3 py-3 text-center text-xs">{row.senior}</td>
                  <td className="px-3 py-3 text-center text-xs">{row.broker}</td>
                  <td className="px-3 py-3 text-center text-xs">{row.owner}</td>
                  <td className="px-3 py-3 text-center text-xs">{row.tenant}</td>
                  <td className="px-3 py-3 text-center text-xs">{row.visitor}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* ── Modals ── */}
      <UserFormModal
        open={showForm}
        onClose={() => { setShowForm(false); setEditUser(null); }}
        onSave={handleUpdate}
        editUser={editUser}
        loading={saving}
      />

      {deleteUser && (
        <DeleteConfirmModal
          user={deleteUser}
          onConfirm={handleDelete}
          onClose={() => setDeleteUser(null)}
          loading={saving}
        />
      )}

      {portfolioUser && (
        <PortfolioModal
          user={portfolioUser}
          locaux={portfolioLoading ? [] : portfolioLocaux}
          onClose={() => { setPortfolioUser(null); setPortfolioLocaux([]); }}
        />
      )}

      {profileUser && (
        <DetailedProfileModal
          user={profileUser}
          onClose={() => setProfileUser(null)}
          onRoleChange={handleRoleChange}
          saving={saving}
        />
      )}
    </div>
  );
};

export default UtilisateursContent;